import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FiTrendingUp,
  FiDollarSign,
  FiBarChart2,
  FiFileText,
  FiMail,
  FiUser,
  FiCalendar
} from 'react-icons/fi';

const InvestorRelationsPage = () => {
  return (
    <div className="bg-gradient-to-b from-gray-900 to-gray-800 min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <HeroSection />
        <InvestmentOpportunity />
        <InvestorInformation />
        <ContactSection />
      </div>
    </div>
  );
};

const HeroSection = () => (
  <motion.section 
    className="text-center max-w-4xl mx-auto mb-28"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
  >
    <div className="inline-block px-4 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-6 border border-blue-800/50">
      Investor Relations
    </div>
    <h1 className="text-4xl sm:text-5xl font-bold mb-4">
      Shape the Future of Finance
    </h1>
    <p className="text-xl text-gray-400 max-w-3xl mx-auto">
      Join Laani in revolutionizing global finance through blockchain innovation and decentralized solutions.
    </p>
  </motion.section>
);

const InvestmentOpportunity = () => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleContactClick = () => {
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 5000);
  };

  return (
    <motion.section 
      className="bg-gray-800/30 p-12 rounded-2xl border border-gray-700/50 mb-28 max-w-5xl mx-auto text-center relative"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, margin: "-100px" }}
    >
      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-full">
        <FiTrendingUp className="w-6 h-6 text-white" />
      </div>
      
      <h2 className="text-3xl font-semibold mb-6">Strategic Investment Opportunity</h2>
      <p className="text-gray-400 text-lg mb-8 max-w-3xl mx-auto">
        Laani is at the forefront of blockchain innovation with a proven track record and ambitious growth roadmap. 
        We're seeking visionary investors to join us in building the future of decentralized finance.
      </p>
      
      <motion.button
        onClick={handleContactClick}
        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all text-white px-8 py-4 rounded-lg font-medium shadow-lg hover:shadow-xl"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Connect with Investor Relations
      </motion.button>
      
      {showSuccessMessage && (
        <motion.p 
          className="mt-6 text-green-400 bg-green-900/30 inline-block px-4 py-2 rounded-lg border border-green-800/50"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
        >
          Thank you for your interest! Our team will contact you shortly.
        </motion.p>
      )}
    </motion.section>
  );
};

const InvestorInformation = () => {
  const financialEvents = [
    {
      date: '2024-04-10',
      event: 'Q1 2024 Earnings Release',
      details: '/investors/q1-2024-earnings',
      icon: <FiDollarSign className="w-4 h-4" />
    },
    {
      date: '2024-03-15',
      event: 'Series B Funding Round',
      details: '/investors/series-b',
      icon: <FiBarChart2 className="w-4 h-4" />
    },
    {
      date: '2024-02-28',
      event: 'Annual Report 2023',
      details: '/investors/annual-report-2023',
      icon: <FiFileText className="w-4 h-4" />
    }
  ];

  return (
    <motion.section 
      className="mb-28 max-w-5xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, margin: "-100px" }}
    >
      <div className="text-center mb-12">
        <h2 className="text-3xl font-semibold mb-4">Financial Information</h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Access our latest financial reports, investor presentations, and key metrics
        </p>
      </div>
      
      <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-8">
          {[
            { title: 'Annual Reports', count: '3', link: '/investors/annual-reports' },
            { title: 'Quarterly Reports', count: '12', link: '/investors/quarterly-reports' },
            { title: 'Investor Presentations', count: '8', link: '/investors/presentations' }
          ].map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -5 }}
              className="bg-gray-800/50 p-6 rounded-lg border border-gray-700/50 hover:border-blue-400/30 transition-all"
            >
              <h3 className="text-xl font-medium mb-3">{item.title}</h3>
              <p className="text-gray-400 mb-4">{item.count} documents available</p>
              <a href={item.link} className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center">
                View all <span className="ml-1">→</span>
              </a>
            </motion.div>
          ))}
        </div>
        
        <div className="border-t border-gray-700/50">
          <h3 className="px-8 pt-6 pb-4 font-medium text-gray-300 flex items-center gap-2">
            <FiCalendar className="w-5 h-5" /> Recent Events
          </h3>
          <div className="divide-y divide-gray-700/50">
            {financialEvents.map((event, index) => (
              <div key={index} className="px-8 py-4 hover:bg-gray-700/20 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-gray-400">{event.date}</span>
                    <span className="flex items-center gap-2">
                      {event.icon} {event.event}
                    </span>
                  </div>
                  <a 
                    href={event.details} 
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center"
                  >
                    Details <span className="ml-1">→</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
};

const ContactSection = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
  };

  return (
    <motion.section 
      className="max-w-3xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true, margin: "-100px" }}
    >
      <div className="bg-gray-800/30 rounded-2xl border border-gray-700/50 p-12 text-center">
        <div className="inline-block px-4 py-1 bg-purple-900/30 rounded-full text-purple-400 text-sm font-medium mb-6 border border-purple-800/50">
          Get in Touch
        </div>
        <h2 className="text-3xl font-semibold mb-4">Investor Relations Contact</h2>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
          Our team is available to answer your questions about investment opportunities and financial information.
        </p>
        
        {formSubmitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-900/30 p-6 rounded-lg border border-green-800/50"
          >
            <h3 className="text-xl font-medium text-green-400 mb-2">Thank You!</h3>
            <p className="text-gray-300">
              We've received your message and will respond within 24-48 hours.
            </p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 text-left max-w-md mx-auto">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-400">Name</label>
              <input
                type="text"
                required
                className="w-full bg-gray-700/50 border border-gray-600/50 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-400">Email</label>
              <input
                type="email"
                required
                className="w-full bg-gray-700/50 border border-gray-600/50 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-400">Company</label>
              <input
                type="text"
                className="w-full bg-gray-700/50 border border-gray-600/50 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-400">Message</label>
              <textarea
                rows="4"
                required
                className="w-full bg-gray-700/50 border border-gray-600/50 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <motion.button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Submit Inquiry
            </motion.button>
          </form>
        )}
      </div>
    </motion.section>
  );
};

export default InvestorRelationsPage;






